import { PersistStorage } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';
import { IErrorMessage } from '@ninja/utilities';
import { CreateJourneyResponse } from '@shory-health-individual/customer-api';
import { JourneyDetails } from '../../server';
import { Dependent, Principal } from '../../server/get-journey-details';
import sharedMiddlewares from '../shared-middlewares';

type MemberSchema = { principalDetails: Principal; dependents?: Dependent[] };
type State = {
	encryptedJourneyId: CreateJourneyResponse['encryptedJourneyId'] | undefined;
	members: (MemberSchema & Pick<JourneyDetails, 'policyType' | 'emirateId' | 'planId'>) | undefined;
	offlineSubmitted: boolean;
	paymentError: IErrorMessage | undefined;
	isManualPayment: boolean;
	documentError:
		| 'DataEntryError'
		| 'InvalidDataError'
		| 'TechnicalError'
		| 'InvalidMemberData'
		| 'WrongEidFormat'
		| undefined;
	pendingUnPaidFinesError: IErrorMessage | undefined;
	isRedirectFromIcp: boolean;
};
type Actions = {
	setJourneyId: (journeyId: string) => void;
	setMembers: (members: State['members']) => void;
	setJourneyPaymentError: (error: IErrorMessage | undefined) => void;
	setIsOfflineSubmitted: (offlineSubmitted: boolean) => void;
	setManualPayment: (isManualPayment: boolean) => void;
	setDocumentError: (
		documentError:
			| 'DataEntryError'
			| 'InvalidDataError'
			| 'TechnicalError'
			| 'InvalidMemberData'
			| 'WrongEidFormat'
			| undefined
	) => void;
	setPendingUnPaidFinesError: (error: IErrorMessage | undefined) => void;
	setIsRedirectFormIcp: (isRedirectFromIcp: boolean) => void;
};

export type Store = State & Actions;

export type QuoteStoreOptions = {
	devtoolsEnabled: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	storage: PersistStorage<any> | undefined;
};

const initialState: State = {
	encryptedJourneyId: undefined,
	members: undefined,
	paymentError: undefined,
	offlineSubmitted: false,
	isManualPayment: false,
	documentError: undefined,
	pendingUnPaidFinesError: undefined,
	isRedirectFromIcp: false,
};

export const createQuoteStore = ({ devtoolsEnabled, storage }: QuoteStoreOptions) =>
	createStore<Store>()(
		sharedMiddlewares(
			(set) => ({
				...initialState,
				setJourneyId: (journeyId) => set({ encryptedJourneyId: journeyId }),
				setMembers: (members) => set({ members }),
				setJourneyPaymentError: (error) => set({ paymentError: error }),
				setIsOfflineSubmitted: (offlineSubmitted) => set({ offlineSubmitted }),
				setManualPayment: (isManualPayment) => set({ isManualPayment }),
				setDocumentError: (documentError) => set({ documentError }),
				setPendingUnPaidFinesError: (error) => set({ pendingUnPaidFinesError: error }),
				setIsRedirectFormIcp: (isRedirectFromIcp) => set({ isRedirectFromIcp }),
			}),
			{ name: 'quote', devtoolsEnabled, storage }
		)
	);
